import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Users, UtensilsCrossed, ShoppingBag, Banknote, TrendingUp,
  Clock, Headphones, Percent, Ban, UserPlus, PackageCheck,
} from 'lucide-react';
import { StatCard, SkeletonCard } from '../components/ui';
import {
  getDashboardStats, getRevenueChartData, getOrderTrendData,
  getPickupStatusBreakdown, getCustomerGrowthData,
} from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency } from '../utils/helpers';

const COLORS = ['#E85D04', '#F48C06', '#10B981', '#3B82F6', '#8B5CF6'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [revenueData, setRevenueData]   = useState([]);
  const [orderData, setOrderData]       = useState([]);
  const [pickupBreakdown, setPickup]    = useState([]);
  const [customerData, setCustomer]     = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [dashData, rev, ord, peak] = await Promise.all([
          adminApi.getDashboard(getDashboardStats),
          adminApi.getRevenueAnalytics('monthly', getRevenueChartData),
          adminApi.getOrderAnalytics('weekly', getOrderTrendData),
          adminApi.getPickupAnalytics(getPickupStatusBreakdown),
        ]);
        if (!cancelled) {
          setStats(dashData);
          setRevenueData(Array.isArray(rev) ? rev : getRevenueChartData());
          setOrderData(Array.isArray(ord) ? ord : getOrderTrendData());
          setPickup(Array.isArray(peak) ? peak : getPickupStatusBreakdown());
          setCustomer(getCustomerGrowthData());
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div>
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Dashboard</h1>
            <p className="admin-page-subtitle">Platform overview and analytics</p>
          </div>
        </div>
        <div className="admin-stat-grid">
          {Array.from({ length: 11 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2 style={{ color: '#E85D04' }}>⚠️ Failed to load dashboard</h2>
        <p style={{ color: 'var(--admin-text-secondary)', marginTop: 8 }}>{error}</p>
        <button className="admin-btn admin-btn-primary" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-subtitle">
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        <div style={{ fontSize: 13, color: 'var(--admin-text-secondary)', background: 'var(--admin-surface)', padding: '8px 16px', borderRadius: 8, border: '1px solid var(--admin-border)' }}>
          📅 {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="admin-stat-grid">
        <StatCard label="Total Users"          value={(stats.totalUsers ?? stats.totalCustomers ?? 0).toLocaleString('en-IN')} icon={<Users size={20} />}         color="#3B82F6" change={12.4} changeLabel="vs last month" />
        <StatCard label="Active Restaurants"   value={(stats.activeRestaurants ?? 0).toLocaleString('en-IN')}                  icon={<UtensilsCrossed size={20} />} color="#E85D04" change={5.2} />
        <StatCard label="Orders Today"         value={(stats.ordersToday ?? 0).toLocaleString('en-IN')}                        icon={<ShoppingBag size={20} />}     color="#8B5CF6" change={8.7} />
        <StatCard label="Revenue Today"        value={formatCurrency(stats.revenueToday)}                                      icon={<Banknote size={20} />}        color="#10B981" change={15.3} />
        <StatCard label="Monthly Revenue"      value={formatCurrency(stats.monthlyRevenue)}                                    icon={<TrendingUp size={20} />}      color="#F59E0B" change={18.4} />
        <StatCard label="Pending Verifications" value={(stats.pendingVerifications ?? 0)}                                      icon={<Clock size={20} />}           color="#F97316" change={-3.1} />
        <StatCard label="Ready for Pickup"     value={(stats.readyForPickupNow ?? 0)}                                          icon={<PackageCheck size={20} />}    color="#06B6D4" />
        <StatCard label="Support Tickets"      value={(stats.supportTickets ?? 0)}                                             icon={<Headphones size={20} />}      color="#EF4444" change={-12.5} />
        <StatCard label="Commission Earnings"  value={formatCurrency(stats.commissionEarnings ?? 0)}                           icon={<Percent size={20} />}         color="#8B5CF6" change={22.1} />
        <StatCard label="Cancellations Today"  value={(stats.cancellationsToday ?? 0)}                                        icon={<Ban size={20} />}             color="#F59E0B" change={-5.4} />
        <StatCard label="New Users This Month" value={(stats.newUsersThisMonth ?? 0).toLocaleString('en-IN')}                  icon={<UserPlus size={20} />}        color="#10B981" change={14.2} />
      </div>

      {/* Revenue Chart */}
      <div className="admin-chart-grid">
        <div className="admin-chart-container" style={{ gridColumn: 'span 2' }}>
          <h3 className="admin-chart-title">📊 Monthly Revenue & Commission (2026)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E85D04" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#E85D04" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="comGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} tickFormatter={v => `$${v >= 1000000 ? (v/1000000).toFixed(1)+"M" : v >= 1000 ? (v/1000).toFixed(0)+"K" : v}`} />
              <Tooltip
                contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }}
                formatter={(v, n) => [`$${v >= 1000000 ? (v/1000000).toFixed(2)+"M" : v >= 1000 ? (v/1000).toFixed(1)+"K" : v}`, n]}
              />
              <Legend />
              <Area type="monotone" dataKey="revenue" stroke="#E85D04" fill="url(#revGrad)" strokeWidth={2} name="Revenue" />
              <Area type="monotone" dataKey="commission" stroke="#10B981" fill="url(#comGrad)" strokeWidth={2} name="Commission" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Order Trends + Pickup Analytics */}
      <div className="admin-chart-grid">
        <div className="admin-chart-container">
          <h3 className="admin-chart-title">📦 Weekly Order Trends</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={orderData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
              <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="orders" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Total Orders" />
              <Bar dataKey="pickedUp" fill="#10B981" radius={[4, 4, 0, 0]} name="Picked Up" />
              <Bar dataKey="cancelled" fill="#EF4444" radius={[4, 4, 0, 0]} name="Cancelled" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-chart-container">
          <h3 className="admin-chart-title">🥡 Pickup Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pickupBreakdown} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name} ${value}`}>
                {pickupBreakdown.map((entry, index) => (
                  <Cell key={index} fill={entry.color || COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Customer Growth */}
      <div className="admin-chart-grid">
        <div className="admin-chart-container" style={{ gridColumn: 'span 2' }}>
          <h3 className="admin-chart-title">👥 Customer Growth Analytics</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={customerData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
              <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="newCustomers" stroke="#E85D04" strokeWidth={2.5} dot={{ r: 4, fill: '#E85D04' }} name="New Customers" />
              <Line type="monotone" dataKey="returning" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 4, fill: '#3B82F6' }} name="Returning Customers" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="admin-card" style={{ padding: 24, marginTop: 8 }}>
        <h3 className="admin-chart-title">⚡ Platform Quick Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginTop: 16 }}>
          {[
            { label: 'Total Orders (All Time)', value: (stats.totalOrders ?? stats.ordersToday ?? 0).toLocaleString('en-IN'), color: '#3B82F6' },
            { label: 'Avg. Order Value',         value: formatCurrency(stats.avgOrderValue ?? 0),                              color: '#10B981' },
            { label: 'Pickup Completion Rate',   value: `${stats.pickupCompletionRate ?? 0}%`,                                 color: '#E85D04' },
            { label: 'Rejections Today',         value: stats.rejectionsToday ?? 0,                                            color: '#EF4444' },
            { label: 'Awaiting Acceptance',      value: stats.ordersAwaitingAccept ?? 0,                                       color: '#F59E0B' },
            { label: 'Commission Rate Avg',      value: '15–20%',                                                              color: '#8B5CF6' },
          ].map((item, i) => (
            <div key={i} style={{
              background: 'var(--admin-bg)', borderRadius: 10, padding: '14px 16px',
              borderLeft: `4px solid ${item.color}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: item.color }}>{item.value}</div>
              <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
